import {
  isValid,
  isBefore,
  differenceInSeconds,
  formatDistanceStrict,
  differenceInDays,
} from 'date-fns';

import {
  formatDate,
  DateFormat,
} from 'src/components/pipelines/utils/formatDate';

import { DateLike, toDate } from './date';

/**
 * Format distance between two Date objects.
 *
 * See https://atlassian.design/content/writing-guidelines/date-and-time-guideline#relative-date-and-time--
 */
export function formatDistance(
  date: DateLike,
  baseDate: DateLike,
  dateFormat: DateFormat = 'date-us-medium'
): string {
  const dateObj = toDate(date);
  const baseObj = toDate(baseDate);

  if (!isValid(dateObj) || !isValid(baseObj)) {
    return 'Invalid Date';
  }

  if (Math.abs(differenceInDays(dateObj, baseObj)) > 7) {
    return formatDate(date, dateFormat);
  }

  /** Positive, in seconds */
  const diff = Math.abs(differenceInSeconds(dateObj, baseObj));

  const inFuture = isBefore(baseObj, dateObj);
  const prefix = inFuture ? 'in ' : '';
  const suffix = !inFuture ? ' ago' : '';

  // The moment formats outputs by a series of "ranges", i.e. "s", "ss", "m", ...
  if (diff <= 44) {
    // s
    return inFuture ? 'shortly' : 'just now';
  } else if (diff <= 89) {
    // m
    return `${prefix}a minute${suffix}`;
  } else if (diff <= 44.5 * 60) {
    // mm, use default
  } else if (diff <= 89.5 * 60) {
    // h
    return `${prefix}an hour${suffix}`;
  } else if (diff <= 21.5 * 3600) {
    // hh, use default
  } else if (diff <= 35.5 * 3600) {
    // d
    return inFuture ? 'tomorrow' : 'yesterday';
  } else if (diff <= 25.5 * 24 * 3600) {
    // dd, use default
  } else if (diff <= 45.5 * 24 * 3600) {
    // M
    return `${prefix}a month${suffix}`;
  } else if (diff <= 319.5 * 24 * 3600) {
    // MM, use default
  } else if (diff <= 547.5 * 24 * 3600) {
    // y
    return `${prefix}a year${suffix}`;
  }
  // else: yy, use default

  // Default date-fns format
  return `${prefix}${formatDistanceStrict(dateObj, baseObj)}${suffix}`;
}

/**
 * Format distance between a Date and current Date.
 *
 * See https://atlassian.design/content/writing-guidelines/date-and-time-guideline#relative-date-and-time--
 */
export function formatDistanceToNow(
  date: DateLike,
  dateFormat: DateFormat = 'date-us-medium'
): string {
  return formatDistance(date, Date.now(), dateFormat);
}
